/** Egypt / Cairo timezone for exam schedule windows. */
export const CAIRO_TZ = "Africa/Cairo";

/**
 * Normalize API datetime to "YYYY-MM-DD HH:mm:ss" (space-separated).
 * @returns {string|null}
 */
export function normalizeApiDateTime(value) {
  if (value == null || String(value).trim() === "") return null;
  let s = String(value).trim().replace("T", " ");
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) {
    s = `${s}:00`;
  }
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) return null;
  return s.slice(0, 19);
}

/**
 * Current wall-clock time in Africa/Cairo as "YYYY-MM-DD HH:mm:ss".
 */
export function getCairoNowString(now = Date.now()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: CAIRO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(now));

  const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
  let hour = get("hour");
  if (hour === "24") hour = "00";

  return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`;
}

/**
 * Parse a Cairo wall-clock API datetime to a UTC epoch ms.
 * @returns {number} 0 if invalid
 */
export function parseCairoWallTimeToMs(apiDateTime) {
  const local = normalizeApiDateTime(apiDateTime);
  if (!local) return 0;
  const isoLocal = local.replace(" ", "T");
  let utc = Date.parse(`${isoLocal}Z`);
  if (!Number.isFinite(utc)) return 0;

  const dtf = new Intl.DateTimeFormat("en-GB", {
    timeZone: CAIRO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const asCairoString = (ms) => {
    const parts = dtf.formatToParts(new Date(ms));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? "00";
    let hour = get("hour");
    if (hour === "24") hour = "00";
    return `${get("year")}-${get("month")}-${get("day")} ${hour}:${get("minute")}:${get("second")}`;
  };

  for (let i = 0; i < 3; i += 1) {
    const shown = asCairoString(utc);
    const shownAsUtc = Date.parse(`${shown.replace(" ", "T")}Z`);
    const desiredAsUtc = Date.parse(`${isoLocal}Z`);
    if (!Number.isFinite(shownAsUtc) || !Number.isFinite(desiredAsUtc)) break;
    utc += desiredAsUtc - shownAsUtc;
  }

  return utc;
}

function formatDurationParts(msRemaining) {
  let seconds = Math.floor(msRemaining / 1000);
  if (seconds <= 0) return null;

  const days = Math.floor(seconds / 86400);
  seconds %= 86400;
  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (days === 0 && hours === 0) {
    if (minutes > 0) parts.push(`${minutes}m`);
    if (minutes < 5 || parts.length === 0) parts.push(`${Math.max(secs, 0)}s`);
  } else if (minutes > 0 || parts.length === 0) {
    parts.push(`${Math.max(minutes, 1)}m`);
  }

  return parts.slice(0, 2).join(" ");
}

/**
 * True when Cairo now is at or after the given API datetime.
 * Empty / missing datetime → false (no deadline).
 */
export function isPastCairoDateTime(apiDateTime, now = Date.now()) {
  const end = normalizeApiDateTime(apiDateTime);
  if (!end) return false;
  return getCairoNowString(now) >= end;
}

/**
 * True when Cairo now is still before the given API datetime.
 * Empty / missing datetime → false (already available).
 */
export function isBeforeCairoDateTime(apiDateTime, now = Date.now()) {
  const start = normalizeApiDateTime(apiDateTime);
  if (!start) return false;
  return getCairoNowString(now) < start;
}

/** Exam / classified assignment has an end_time that has passed (Cairo). */
export function isExamPastEndTime(examOrEndTime, now = Date.now()) {
  const endTime =
    examOrEndTime && typeof examOrEndTime === "object"
      ? examOrEndTime.end_time
      : examOrEndTime;
  return isPastCairoDateTime(endTime, now);
}

/**
 * Human label for time until end_time (Cairo).
 * @returns {string|null} e.g. "Exam closes in 2d 5h"
 */
export function formatExamClosesIn(endTime, now = Date.now()) {
  const endMs = parseCairoWallTimeToMs(endTime);
  if (!endMs || endMs <= now) return null;
  const duration = formatDurationParts(endMs - now);
  if (!duration) return null;
  return `closes in ${duration}`;
}
